import React, { useEffect, useRef, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { useAccount } from '../../context/AccountContext';
import { AgentWork, agentRowWords, agentWorkLink, ago, startedWords } from './agentWork';

export type { AgentWork } from './agentWork';

/**
 * The header's agent light ("tinycms UX optimization review v6", Prototype v7):
 * green "Agent working", pulsing, while the site's agent is using its tools (a
 * call in the last few minutes); a quiet grey dot, with when it was last
 * active, once it has been used; and for a site no agent has connected to yet,
 * a quiet "Connect an agent" that opens Connections. Nothing is broken then,
 * so nothing is red (UX review round 4: a red "Agent connection not found"
 * beside "Your agent drafted this page" read as a contradiction).
 *
 * While it works, the light opens what it's doing (UX review round 11): its
 * name, "Drafting an update to your About page", when it started, a link to
 * the item, and that nothing goes live until the person approves it. Today
 * shows the same line (AgentWorkingRow).
 */

export const AGENT_ACTIVITY = gql`
  query AgentActivity($accountId: ID!) {
    agentActivity(accountId: $accountId) {
      lastCallAt
      callsLastHour
      connected
      working {
        agentName
        doing
        startedAt
        kind
        subjectId
        subjectTitle
      }
    }
  }
`;

const WORKING_MS = 5 * 60 * 1000;

type AgentActivity = {
  agentActivity: {
    lastCallAt?: string | null;
    callsLastHour: number;
    connected: boolean;
    working?: AgentWork | null;
  };
};

/** The agent's run from the same query as the header light (null when it isn't working). */
export function useAgentWork(): AgentWork | null {
  const { accountId } = useAccount();
  const { data } = useQuery<AgentActivity>(AGENT_ACTIVITY, {
    variables: { accountId },
    skip: !accountId,
    pollInterval: 60000,
    fetchPolicy: 'cache-and-network',
  });
  const a = data?.agentActivity;
  if (!a?.working || !a.lastCallAt) return null;
  return Date.now() - new Date(a.lastCallAt).getTime() < WORKING_MS ? a.working : null;
}

/**
 * Whether an agent has ever connected to the site: null until known. Screens
 * say "Your agent drafted this" only when one has — nothing records who made
 * a page's or form's change, and a starter's seeded pages aren't an agent's.
 */
export function useAgentConnected(): boolean | null {
  const { accountId } = useAccount();
  const { data } = useQuery<AgentActivity>(AGENT_ACTIVITY, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-first',
  });
  return data ? data.agentActivity.connected : null;
}

export function AgentStatus() {
  const { accountId } = useAccount();
  const { data } = useQuery<AgentActivity>(AGENT_ACTIVITY, {
    variables: { accountId },
    skip: !accountId,
    pollInterval: 60000,
    fetchPolicy: 'cache-and-network',
  });
  if (!data) return null; // nothing until we know, so it never flashes the wrong state
  if (!data.agentActivity.connected) {
    const label = 'Connect an agent';
    return (
      <Link
        to="/settings/ai-agents"
        className="ink-agent"
        data-state="none"
        title="No agent has connected to this site yet. Open AI Agents to set one up."
        aria-label={label}
        style={{ textDecoration: 'none' }}
      >
        <span className="ink-agent-dot" />
        <span className="ink-agent-label">{label}</span>
      </Link>
    );
  }
  const last = data.agentActivity.lastCallAt ? new Date(data.agentActivity.lastCallAt) : null;
  if (!last || Number.isNaN(last.getTime())) return null;
  const since = Date.now() - last.getTime();
  const working = since < WORKING_MS;
  const label = working ? 'Agent working' : `Agent last active ${ago(since)}`;
  const work = working ? data.agentActivity.working : null;
  if (work) return <AgentWorking work={work} />;
  return (
    <span
      className="ink-agent"
      data-working={working ? 'true' : 'false'}
      title={label}
      role="status"
      aria-label={label}
    >
      <span className="ink-agent-dot" />
      {working ? <span className="ink-agent-label">Agent working</span> : null}
    </span>
  );
}

/** "Agent working", and what it's doing on click (UX review round 11). */
function AgentWorking({ work }: { work: AgentWork }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  const button = useRef<HTMLButtonElement>(null);

  // Escape (focus back to the light), a click outside, or leaving closes it.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      button.current?.focus();
    };
    const onDown = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const link = agentWorkLink(work);
  const when = startedWords(work.startedAt);
  return (
    <span className="ink-agent-wrap" ref={wrap}>
      <button
        ref={button}
        type="button"
        className="ink-agent ink-agent-btn"
        data-working="true"
        aria-expanded={open}
        aria-haspopup="dialog"
        title="Agent working. See what it’s doing"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="ink-agent-dot" />
        <span className="ink-agent-label">Agent working</span>
      </button>
      {open ? (
        <div className="ink-agent-pop" role="dialog" aria-label="What your agent is doing">
          <div className="ink-agent-pop-who">
            <span className="ink-agent-dot" />
            {work.agentName?.trim() || 'Your agent'} · working now
          </div>
          <div className="ink-agent-pop-doing">{work.doing}</div>
          {when.clock ? (
            <div className="ink-agent-pop-when">
              Started {when.clock} · {when.ago}
            </div>
          ) : null}
          {link ? (
            <Link to={link.to} className="ink-agent-pop-link" onClick={() => setOpen(false)}>
              {link.label} →
            </Link>
          ) : null}
          <div className="ink-agent-pop-note">
            Nothing goes live until you approve it. When it’s done, it waits in Today.
          </div>
        </div>
      ) : null}
    </span>
  );
}

/**
 * Today's quiet line while the agent works (UX review round 11): "Your agent is
 * working on an update to your About page · started 18 min ago · Open". "All
 * caught up" can stay true beside it: nothing waits on the person yet.
 */
export function AgentWorkingRow() {
  const work = useAgentWork();
  if (!work) return null;
  const link = agentWorkLink(work);
  const when = startedWords(work.startedAt);
  return (
    <div className="ink-agent-row" role="status">
      <span className="ink-agent-dot" data-working="true" />
      <span className="ink-agent-row-text">
        <strong>Your agent is {agentRowWords(work.doing)}</strong>
        {when.ago ? <span className="ink-muted"> · started {when.ago}</span> : null}
      </span>
      {link ? (
        <Link to={link.to} className="ink-agent-row-open">
          Open →
        </Link>
      ) : null}
    </div>
  );
}
